import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Command as CommandPrimitive } from 'cmdk';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { SearchIcon, UserRoundIcon } from 'lucide-react';
import { KeyboardShortcut } from '@gokrew/ui/components/KeyboardShortcut';
import {
  InviteSuccessDialog,
  type InviteCredentials,
} from '@gokrew/ui/components/InviteSuccessDialog';
import { TranslationNS } from '@/constants/translation-namespaces';
import { useCommandPalette } from '@/context/useCommandPaletteContext';
import { usePermissions } from '@/context/PermissionContext';
import { useEmployees } from '@/hooks/employees/useEmployee';
import { useDebounce } from '@/hooks/useDebounce';
import { permissionKeys } from '@/utility';
import {
  useCommandPaletteCommands,
  type PaletteCommand,
} from './useCommandPaletteCommands';
import styles from './CommandPalette.module.css';

const CHORD_PREFIX = 'g';
const CHORD_WINDOW_MS = 1200;
const MIN_SEARCH_LENGTH = 2;
const PEOPLE_LIMIT = 6;

/** Typing somewhere: single-letter shortcuts must stay out of the way. */
const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
  );
};

/** Another overlay (dialog, menu, picker) owns the keyboard right now. */
const isOverlayOpen = () =>
  document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]') !==
  null;

const CommandRow = ({
  command,
  onSelect,
}: {
  command: PaletteCommand;
  onSelect: (command: PaletteCommand) => void;
}) => {
  const { t } = useTranslation(TranslationNS.Common);
  return (
    <CommandPrimitive.Item
      value={`${command.id} ${command.label} ${command.keywords.join(' ')}`}
      onSelect={() => onSelect(command)}
      className={styles.item}
    >
      <command.icon className={styles.itemIcon} aria-hidden />
      <span className={styles.itemLabel}>{command.label}</span>
      {command.key && <KeyboardShortcut keys={[command.key.toUpperCase()]} />}
      {command.chord && (
        <span
          className={styles.chord}
          aria-label={t('command_palette.chord_aria', {
            key: command.chord.toUpperCase(),
          })}
        >
          <KeyboardShortcut keys={[CHORD_PREFIX.toUpperCase()]} />
          <span className={styles.chordThen}>{t('command_palette.then')}</span>
          <KeyboardShortcut keys={[command.chord.toUpperCase()]} />
        </span>
      )}
    </CommandPrimitive.Item>
  );
};

/**
 * The ⌘K / Ctrl+K palette, mounted once in the main layout: quick actions an
 * HR admin repeats all day, a jump to any page, and a people search. The same
 * file owns the "G then …" page jumps, so every shortcut shown in the list
 * actually works from anywhere in the portal.
 */
export const CommandPalette = () => {
  const { open, setOpen, toggle } = useCommandPalette();
  const navigate = useNavigate();
  const { t } = useTranslation(TranslationNS.Common);
  const { permissions } = usePermissions();
  const [search, setSearch] = useState('');
  // "/" hands the keyboard back to the search box (see onInputKeyDown).
  const [searchMode, setSearchMode] = useState(false);
  const [inviteCredentials, setInviteCredentials] =
    useState<InviteCredentials | null>(null);

  const commands = useCommandPaletteCommands({
    onInviteSuccess: setInviteCredentials,
  });
  const actions = commands.filter((c) => c.group === 'actions');
  const pages = commands.filter((c) => c.group === 'navigate');

  const canSearchPeople = permissions.has(permissionKeys.employee.read);
  const debouncedSearch = useDebounce(search.trim(), 250);
  const { data: peopleResponse } = useEmployees(
    { page: 1, limit: PEOPLE_LIMIT, search: debouncedSearch },
    {
      enabled:
        open && canSearchPeople && debouncedSearch.length >= MIN_SEARCH_LENGTH,
    }
  );
  const people =
    canSearchPeople && debouncedSearch.length >= MIN_SEARCH_LENGTH
      ? (peopleResponse?.data?.data ?? [])
      : [];

  // Run from a ref so the keydown listener never re-binds mid-chord.
  const commandsRef = useRef(commands);
  commandsRef.current = commands;
  const pendingChord = useRef<number | null>(null);

  const runCommand = useCallback(
    (command: PaletteCommand) => {
      setOpen(false);
      // Let the palette's own dialog finish closing first, or its focus
      // restore lands on top of the dialog the command is about to open.
      window.setTimeout(command.run, 0);
    },
    [setOpen]
  );

  const openPerson = useCallback(
    (employeeId: string) => {
      setOpen(false);
      navigate(`/employees/detail/${employeeId}/info`);
    },
    [navigate, setOpen]
  );

  useEffect(() => {
    const clearChord = () => {
      if (pendingChord.current !== null) {
        window.clearTimeout(pendingChord.current);
        pendingChord.current = null;
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      // ⌘K / Ctrl+K from anywhere. A rich-text editor keeps its own link
      // shortcut, so only an editable surface that is not a plain field yields.
      if (
        event.code === 'KeyK' &&
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        !event.altKey
      ) {
        const target = event.target;
        if (
          target instanceof HTMLElement &&
          target.isContentEditable &&
          !target.closest('[cmdk-root]')
        ) {
          return;
        }
        event.preventDefault();
        clearChord();
        toggle();
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target) || isOverlayOpen()) {
        clearChord();
        return;
      }

      const key = event.key.toLowerCase();
      if (pendingChord.current !== null) {
        clearChord();
        const match = commandsRef.current.find((c) => c.chord === key);
        if (match) {
          event.preventDefault();
          match.run();
        }
        return;
      }
      if (key === CHORD_PREFIX) {
        pendingChord.current = window.setTimeout(clearChord, CHORD_WINDOW_MS);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearChord();
    };
  }, [toggle]);

  /**
   * With an empty search box a quick action's letter runs it at once (E adds
   * an employee), and "/" switches to plain searching, since a letter that is
   * an action could otherwise never start a search.
   */
  const onInputKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (search !== '' || searchMode) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.nativeEvent.isComposing) return;
      if (event.key === '/') {
        event.preventDefault();
        setSearchMode(true);
        return;
      }
      const match = commandsRef.current.find(
        (c) => c.key === event.key.toLowerCase()
      );
      if (match) {
        event.preventDefault();
        runCommand(match);
      }
    },
    [search, searchMode, runCommand]
  );

  const onOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) {
        setSearch('');
        setSearchMode(false);
      }
    },
    [setOpen]
  );

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className={styles.overlay} />
          <DialogPrimitive.Content
            className={styles.content}
            aria-describedby={undefined}
          >
            <DialogPrimitive.Title className={styles.srOnly}>
              {t('command_palette.title')}
            </DialogPrimitive.Title>
            <CommandPrimitive className={styles.command} loop>
              <div className={styles.inputRow}>
                <SearchIcon className={styles.searchIcon} aria-hidden />
                <CommandPrimitive.Input
                  value={search}
                  onValueChange={setSearch}
                  onKeyDown={onInputKeyDown}
                  placeholder={t(
                    searchMode
                      ? canSearchPeople
                        ? 'command_palette.placeholder'
                        : 'command_palette.placeholder_no_people'
                      : 'command_palette.placeholder_keys'
                  )}
                  className={styles.input}
                />
              </div>
              <CommandPrimitive.List className={styles.list}>
                <CommandPrimitive.Empty className={styles.empty}>
                  {t('command_palette.empty')}
                </CommandPrimitive.Empty>

                {people.length > 0 && (
                  <CommandPrimitive.Group
                    heading={t('command_palette.groups.people')}
                    className={styles.group}
                  >
                    {people.map((person) => {
                      const name = `${person.user.firstName} ${person.user.lastName}`;
                      return (
                        <CommandPrimitive.Item
                          key={person.id}
                          forceMount
                          value={`person-${person.id}`}
                          onSelect={() => openPerson(person.id)}
                          className={styles.item}
                        >
                          <UserRoundIcon
                            className={styles.itemIcon}
                            aria-hidden
                          />
                          <span className={styles.itemLabel}>{name}</span>
                          <span className={styles.itemMeta}>
                            {person.designation?.name ?? person.workEmail}
                          </span>
                        </CommandPrimitive.Item>
                      );
                    })}
                  </CommandPrimitive.Group>
                )}

                {actions.length > 0 && (
                  <CommandPrimitive.Group
                    heading={t('command_palette.groups.actions')}
                    className={styles.group}
                  >
                    {actions.map((command) => (
                      <CommandRow
                        key={command.id}
                        command={command}
                        onSelect={runCommand}
                      />
                    ))}
                  </CommandPrimitive.Group>
                )}

                {pages.length > 0 && (
                  <CommandPrimitive.Group
                    heading={t('command_palette.groups.navigate')}
                    className={styles.group}
                  >
                    {pages.map((command) => (
                      <CommandRow
                        key={command.id}
                        command={command}
                        onSelect={runCommand}
                      />
                    ))}
                  </CommandPrimitive.Group>
                )}
              </CommandPrimitive.List>
              <div className={styles.footer} aria-hidden>
                <span className={styles.footerHint}>
                  <KeyboardShortcut keys={['↑']} />
                  <KeyboardShortcut keys={['↓']} />
                  {t('command_palette.hints.move')}
                </span>
                <span className={styles.footerHint}>
                  <KeyboardShortcut keys={['enter']} />
                  {t('command_palette.hints.select')}
                </span>
                <span className={styles.footerHint}>
                  <KeyboardShortcut keys={['esc']} />
                  {t('command_palette.hints.close')}
                </span>
              </div>
            </CommandPrimitive>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      {inviteCredentials && (
        <InviteSuccessDialog
          open
          onClose={() => setInviteCredentials(null)}
          credentials={inviteCredentials}
          portalLabel="Employee"
        />
      )}
    </>
  );
};
